import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import type {
  PictureUploadResponse,
  PictureResponse,
  PendingFile,
} from '../interfaces/picture.dto';
import { PICTURE_SERVICE_FE_CONFIG } from './picture-service-fe.config';

type StoredKeycloakTokens = {
  accessToken?: string;
};

@Injectable({
  providedIn: 'root',
})
export class PictureServiceFe {
  private readonly http = inject(HttpClient);
  private readonly config = inject(PICTURE_SERVICE_FE_CONFIG, {
    optional: true,
  });

  /**
   * Upload a single picture for a recipe
   * @param file - The image file to upload
   * @param recipeId - The recipe this picture belongs to
   * @param caption - Optional caption for the picture
   */
  uploadPicture(
    file: File,
    recipeId: string,
    caption?: string,
  ): Observable<PictureUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('recipe_id', recipeId);
    if (caption) {
      formData.append('caption', caption);
    }

    return this.http.post<PictureUploadResponse>(
      `${this.getEndpoint()}/api/v1/pictures/upload`,
      formData,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Upload multiple pictures for a recipe
   * @param pendingFiles - Array of files with optional captions
   * @param recipeId - The recipe these pictures belong to
   * @returns Observable of all uploaded pictures
   */
  uploadMultiplePictures(
    pendingFiles: PendingFile[],
    recipeId: string,
  ): Observable<PictureUploadResponse[]> {
    if (pendingFiles.length === 0) {
      return of([]);
    }

    const uploads = pendingFiles.map((pf) =>
      this.uploadPicture(pf.file, recipeId, pf.caption),
    );

    return forkJoin(uploads);
  }

  /**
   * Get all pictures for a recipe
   * @param recipeId - The recipe ID to fetch pictures for
   */
  getPicturesByRecipe(recipeId: string): Observable<PictureResponse[]> {
    return this.http
      .get<{ pictures: PictureResponse[] }>(
        `${this.getEndpoint()}/api/v1/pictures`,
        {
          params: { recipe_id: recipeId },
        },
      )
      .pipe(map((response) => response.pictures));
  }

  /**
   * Get a single picture by ID
   * @param pictureId - The picture ID
   */
  getPictureById(pictureId: string): Observable<PictureResponse> {
    return this.http.get<PictureResponse>(
      `${this.getEndpoint()}/api/v1/pictures/${pictureId}`,
    );
  }

  /**
   * Delete a picture (placeholder - backend not implemented yet)
   * @param pictureId - The picture ID to delete
   */
  deletePicture(pictureId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.getEndpoint()}/api/v1/pictures/${pictureId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  private getAuthHeaders(): HttpHeaders {
    let headers = new HttpHeaders();

    const accessToken = this.getStoredAccessToken();
    if (!accessToken) {
      return headers;
    }

    headers = headers.set('authorization', `Bearer ${accessToken}`);

    const userId = this.getUserIdFromToken(accessToken);
    if (userId) {
      headers = headers.set('x-user-id', userId);
    }

    return headers;
  }

  private getStoredAccessToken(): string | null {
    try {
      const raw = sessionStorage.getItem('erb_kc_tokens');
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as StoredKeycloakTokens;
      const token = parsed.accessToken?.trim();
      return token && token.length > 0 ? token : null;
    } catch {
      return null;
    }
  }

  private getUserIdFromToken(token: string): string | null {
    try {
      const parts = token.split('.');
      if (parts.length < 2) {
        return null;
      }

      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded)) as { sub?: string };
      const userId = payload.sub?.trim();
      return userId && userId.length > 0 ? userId : null;
    } catch {
      return null;
    }
  }

  private getEndpoint(): string {
    if (!this.config?.endpoint) {
      throw new Error(
        'PictureServiceFe: missing endpoint configuration. Call providePictureServiceFe() in your app config.',
      );
    }
    return this.config.endpoint;
  }
}
